import PropTypes from 'prop-types';

import Modal from '#globalComponents/Overlay/Modal';
import { useAuth } from '#globalContexts/AuthProvider';
import { useCancellationFlow } from '#settingsFeature/hooks/useCancellationFlow';

import ConfirmCancelStep from './steps/ConfirmCancelStep';
import DoneStep from './steps/DoneStep';
import OfferStep from './steps/OfferStep';
import PauseStep from './steps/PauseStep';
import ReasonStep from './steps/ReasonStep';

function StepContent({ flow, subscription, isPaused }) {
	const onBack = flow.canGoBack ? flow.back : undefined;
	const wasCancelling = Boolean(subscription.cancelAtPeriodEnd);
	const onClose = flow.isPending ? undefined : flow.close;

	switch (flow.step) {
		case 'reason':
			return (
				<ReasonStep
					reason={flow.reason}
					comment={flow.comment}
					onReasonChange={flow.setReason}
					onCommentChange={flow.setComment}
					onNext={flow.goNext}
					onAbort={flow.close}
					onClose={onClose}
				/>
			);
		case 'offer':
			return (
				<OfferStep
					offerType={flow.offer.type}
					reason={flow.reason}
					onCancelAnyway={flow.declineOffer}
					onBack={onBack}
					onClose={onClose}
				/>
			);
		case 'pause':
			// An already-cancelling subscription cannot be cancelled again, so the escape keeps that cancellation.
			return (
				<PauseStep
					pauseOptions={subscription.retention?.pauseOptions ?? []}
					months={flow.pauseMonths}
					onMonthsChange={flow.setPauseMonths}
					onConfirm={flow.submitPause}
					onCancelAnyway={wasCancelling ? flow.close : flow.goToConfirm}
					cancelAnywayLabel={wasCancelling ? 'Kündigung beibehalten' : undefined}
					wasCancelling={wasCancelling}
					onBack={onBack}
					isLoading={flow.isPending}
					onClose={onClose}
				/>
			);
		case 'confirm':
			// Last-chance pause; hidden when the user already saw the pause step or gave a reason
			// pausing cannot fix (switching, business closed — resolveOffer returns type null for those).
			return (
				<ConfirmCancelStep
					currentPeriodEnd={subscription.currentPeriodEnd ?? null}
					pendingPlanName={subscription.pendingPrice?.products?.name ?? null}
					pause={subscription.pause ?? null}
					isPaused={isPaused}
					onKeep={flow.close}
					onConfirm={flow.submitCancel}
					onPause={
						flow.pauseEligible && !flow.hasSeenPause && flow.offer.type !== null
							? flow.goToPause
							: undefined
					}
					isLoading={flow.isPending}
					onClose={onClose}
				/>
			);
		case 'done':
			return (
				<DoneStep
					kind={flow.result.kind}
					subscription={flow.result.subscription}
					wasCancelling={wasCancelling}
					onClose={flow.close}
				/>
			);
		default:
			return null;
	}
}

StepContent.propTypes = {
	flow: PropTypes.object.isRequired,
	subscription: PropTypes.object.isRequired,
	isPaused: PropTypes.bool.isRequired,
};

// Mounted only while open (the parent conditionally renders it), so the flow state resets by unmounting.
export const ManageSubscriptionModal = ({ onClose, subscription, initialStep = 'reason' }) => {
	const { orgPlan } = useAuth();
	const flow = useCancellationFlow({ subscription, initialStep, onClose });

	return (
		<Modal isOpen onClose={flow.isPending ? undefined : flow.close} size="m">
			<StepContent flow={flow} subscription={subscription} isPaused={orgPlan === 'paused'} />
		</Modal>
	);
};

ManageSubscriptionModal.propTypes = {
	onClose: PropTypes.func.isRequired,
	subscription: PropTypes.object.isRequired,
	initialStep: PropTypes.oneOf(['reason', 'pause']),
};

export default ManageSubscriptionModal;
